import api from './api';

export const leadService = {
  // ========== LEAD CRUD ==========
  async getAll(status = null) {
    const params = status ? { status } : {};
    const response = await api.get('/leads', { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/leads/${id}`);
    return response.data;
  },

  async create(leadData) {
    const response = await api.post('/leads', leadData);
    return response.data;
  },

  async update(id, leadData) {
    const response = await api.put(`/leads/${id}`, leadData);
    return response.data;
  },

  async delete(id) {
    await api.delete(`/leads/${id}`);
  },

  // ========== INTERACTIONS ==========
  async getInteractions(leadId) {
    const response = await api.get(`/leads/${leadId}/interacoes`);
    return response.data;
  },

  async createInteraction(leadId, interactionData) {
    const response = await api.post(`/leads/${leadId}/interacoes`, interactionData);
    return response.data;
  },

  // ========== PROPOSALS ==========
  async getProposals(leadId) {
    const response = await api.get(`/leads/${leadId}/propostas`);
    return response.data;
  },

  async createProposal(leadId, proposalData) {
    const response = await api.post(`/leads/${leadId}/propostas`, proposalData);
    return response.data;
  },

  async updateProposal(leadId, proposalId, proposalData) {
    const response = await api.put(`/leads/${leadId}/propostas/${proposalId}`, proposalData);
    return response.data;
  },

  async deleteProposal(leadId, proposalId) {
    await api.delete(`/leads/${leadId}/propostas/${proposalId}`);
  },

  // ========== CONVERSION ==========
  async convertToClient(leadId) {
    const response = await api.post(`/leads/${leadId}/converter`);
    return response.data;
  },

  // ========== DASHBOARD ==========
  async getDashboardStats(startDate = null, endDate = null) {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await api.get('/leads/dashboard/stats', { params });
    return response.data;
  },

  async getAlerts() {
    const response = await api.get('/leads/dashboard/alertas');
    return response.data;
  },

  // ========== PROPOSAL FILE MANAGEMENT ==========
  async uploadProposalFile(proposalId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/leads/propostas/${proposalId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      throw error;
    }
  },

  async downloadProposalFile(proposalId, fileName) {
    try {
      const response = await api.get(`/leads/propostas/${proposalId}/download`, {
        responseType: 'blob',
      });

      // Criar link temporário para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'proposta.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      throw error;
    }
  },

  async deleteProposalFile(proposalId) {
    try {
      const response = await api.delete(`/leads/propostas/${proposalId}/file`);
      return response.data;
    } catch (error) {
      console.error('Erro ao deletar arquivo:', error);
      throw error;
    }
  },

  formatFileSize(bytes) {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  },

  validateFile(file) {
    const maxSize = 50 * 1024 * 1024; // 50 MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/png',
      'image/jpeg'
    ];

    if (file.size > maxSize) {
      return { valid: false, error: 'Arquivo muito grande. Máximo: 50 MB' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Tipo de arquivo não permitido. Use PDF, DOC, DOCX, XLS, XLSX, TXT, PNG ou JPG' };
    }

    return { valid: true };
  },

  // ========== UTILITY METHODS ==========
  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  },

  translateStatus(status) {
    const translations = {
      'novo': 'Novo',
      'em_contato': 'Em Contato',
      'qualificado': 'Qualificado',
      'proposta_enviada': 'Proposta Enviada',
      'negociacao': 'Negociação',
      'ganho': 'Ganho',
      'perdido': 'Perdido',
      'cancelado': 'Cancelado'
    };
    return translations[status] || status;
  },

  getStatusColor(status) {
    const colors = {
      'novo': 'bg-blue-100 text-blue-800',
      'em_contato': 'bg-purple-100 text-purple-800',
      'qualificado': 'bg-cyan-100 text-cyan-800',
      'proposta_enviada': 'bg-yellow-100 text-yellow-800',
      'negociacao': 'bg-orange-100 text-orange-800',
      'ganho': 'bg-green-100 text-green-800',
      'perdido': 'bg-red-100 text-red-800',
      'cancelado': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  },

  daysSinceLastInteraction(lastInteractionDate) {
    if (!lastInteractionDate) return null;
    const now = new Date();
    const lastDate = new Date(lastInteractionDate);
    const diffTime = Math.abs(now - lastDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  },
};
